import { IconRegularClose, Modal, ModalModel, SkeletonElement } from '@notino/react-styleguide'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'

// types
import { SalonEmployeesResponse } from '../../../../types/types'

// utils
import { AppContext } from '../../../../utils/appProvider'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'

// hooks
import useMessages from '../../../../hooks/useMessages'

// styles
import * as SC from './EmployeesModalStyles'

type EmployeesModalProps = {
	salonID: string
	onClose: () => void
}

const LIMIT = 100
const LOADING_SKELETONS_COUNT = 10
const LOADING_MORE_SKELETONS_COUNT = 3

const EmployeesModal = (props: EmployeesModalProps) => {
	const { salonID, onClose } = props
	const { apiBrowser } = useContext(AppContext)
	const { messages } = useMessages()

	const [employees, setEmployees] = useState<SalonEmployeesResponse>()
	const [isLoading, setIsLoading] = useState(true)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [isError, setIsError] = useState(false)
	const [isLoadingMoreError, setIsLoadingMoreError] = useState(false)

	const scrollableContent = useRef<HTMLDivElement>(null)
	const skeletonRef = useRef<HTMLLIElement>(null)

	const fetchEmployees = useCallback(
		async (data: { page: number }) => {
			setIsError(false)
			setIsLoadingMoreError(false)

			if (data.page === 1) {
				setIsLoading(true)
			} else {
				setIsLoadingMore(true)
			}

			try {
				const response = await apiBrowser.b2c.getSalonEmployees(salonID, { page: data.page, limit: LIMIT })

				setEmployees((prev) => ({
					employees: [...(prev?.employees ?? []), ...response.employees],
					pagination: response.pagination
				}))
			} catch (error) {
				if (data.page === 1) {
					setIsError(true)
				} else {
					setIsLoadingMoreError(true)
				}

				// eslint-disable-next-line no-console
				console.error('Error fetching employees:', error)
			} finally {
				if (data.page === 1) {
					setIsLoading(false)
				} else {
					setIsLoadingMore(false)
				}
			}
		},
		[apiBrowser.b2c, salonID]
	)

	useEffect(() => {
		fetchEmployees({ page: 1 })
	}, [fetchEmployees])

	useEffect(() => {
		if (!skeletonRef.current || isLoadingMore || isLoadingMoreError) {
			return undefined
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && employees && employees.pagination.page < employees.pagination.totalPages) {
					fetchEmployees({ page: employees.pagination.page + 1 })
				}
			},
			{ root: scrollableContent.current, threshold: 0.1 }
		)

		observer.observe(skeletonRef.current)

		return () => observer.disconnect()
	}, [employees, isLoadingMore, isLoadingMoreError, fetchEmployees])

	const getContent = () => {
		if (isLoading) {
			return Array.from({ length: LOADING_SKELETONS_COUNT }).map((_, index) => (
				<SC.ElementsListItem key={index}>
					<SC.SkeletonAvatarElement width={'48px'} height={'48px'} />
					<SkeletonElement width={'120px'} height={'1.25rem'} />
				</SC.ElementsListItem>
			))
		}

		if (isError) {
			return <ErrorScreen button={{ onClick: () => fetchEmployees({ page: 1 }) }} />
		}

		if (!employees || employees?.employees.length === 0) {
			return <Empty />
		}

		return (
			<>
				{employees.employees.map((employee) => (
					<SC.ElementsListItem key={employee.id}>
						<SC.ElementsListItemImageContainer>
							<img alt={employee.firstName} src={employee.image.resizedImages.thumbnail} width={48} height={48} />
						</SC.ElementsListItemImageContainer>
						{employee.firstName}
					</SC.ElementsListItem>
				))}
				{employees.pagination.page < employees.pagination.totalPages &&
					(!isLoadingMoreError ? (
						Array.from({ length: LOADING_MORE_SKELETONS_COUNT }).map((_, index) => (
							<SC.ElementsListItem key={index} ref={index === 0 ? skeletonRef : undefined}>
								<SC.SkeletonAvatarElement width={'48px'} height={'48px'} />
								<SkeletonElement width={'120px'} height={'1.25rem'} />
							</SC.ElementsListItem>
						))
					) : (
						<ErrorScreen button={{ onClick: () => fetchEmployees({ page: employees.pagination.page + 1 }) }} />
					))}
			</>
		)
	}

	return (
		<SC.ModalWrapper>
			<Modal show onClose={onClose} noBorders fullscreenOnMobile breakpoint={ModalModel.ModalBreakpoints.md}>
				<SC.ModalContentWrapper>
					<SC.ModalHeader>
						{messages.Team}
						<SC.CloseButton type={'button'} onClick={() => onClose()}>
							<IconRegularClose color={'icon.primary'} />
						</SC.CloseButton>
					</SC.ModalHeader>
					<SC.ModalMain ref={scrollableContent}>{getContent()}</SC.ModalMain>
				</SC.ModalContentWrapper>
			</Modal>
		</SC.ModalWrapper>
	)
}

export default EmployeesModal
